import React, { useEffect, useMemo, useState, useContext, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  UserIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  PhoneArrowUpRightIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { getBproCalendar, getBproSucursales, listAppointments } from '../../services/appointmentsApi';
import AuthContext from '../../contexts/AuthContext';
import AdvisorReviewQueue from './AdvisorReviewQueue';
import AdvisorReviewPanel from './AdvisorReviewPanel';
import FollowupQueue from './FollowupQueue';
import { hasReviewAccess, reviewIdentity } from './advisorReview';
import { resolveCitaLinks } from './citaLinks';
import { bookingExtrasEntries } from './bookingExtras';
import DatosDisputeMarker from './DatosDisputeMarker';
import { formatDatosUpdates } from './datosUpdates';
import {
  SOURCE_AIVOICE,
  SOURCE_BPRO,
  DEPT_VENTAS,
  DEPT_POSTVENTA,
  SUCURSAL_UNKNOWN,
  tagAppointment,
  filterAppointments,
  computeCounts,
  computeSucursalCounts,
  syncedAgoLabel,
  bproSyncedCount,
  bproSyncStatus,
  loadFilters,
  saveFilters,
  sucursalAccent,
  deriveSucursalKey,
  deriveSucursalOptions,
  deriveSucursalChipOptions,
  filterBySucursalChips,
  sucursalChipOn,
  loadSucursalFilters,
  saveSucursalFilters,
} from './calendarTags';

// B.PRO remains READ-ONLY and the source of truth for the shared agenda; the
// native DynamoDB appointments table (created by our own AI stack) is loaded here
// too ONLY so the calendar can visibly distinguish where each cita came from
// (feat-calendar-source-department-filters-sep1). No booking logic changes; no
// writes to B.PRO. Filtering is client-side over the already-loaded list.
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

function buildMonthGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const cells = Array(first.getDay()).fill(null);
  const count = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  for (let day = 1; day <= count; day += 1) {
    cells.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// Parse a loaded row into a JS Date. Accepts ISO (YYYY-MM-DD) AND the B.PRO
// legacy DD/MM/YYYY string (CIT_FECCITA is a varchar in dd/mm/yyyy). Before this,
// a dd/mm/yyyy date failed the ISO-only regex and the B.PRO cita silently vanished
// from the grid (verified live 2026-09-02); the backend now emits ISO, but we stay
// tolerant here as defense-in-depth so no valid cita is ever dropped.
const appointmentDate = (appointment) => {
  const raw = String(appointment.date || '');
  let y; let mo; let da;
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (iso) {
    y = Number(iso[1]); mo = Number(iso[2]); da = Number(iso[3]);
  } else {
    const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);
    if (dmy) { da = Number(dmy[1]); mo = Number(dmy[2]); y = Number(dmy[3]); }
  }
  const time = /^(\d{1,2}):(\d{2})/.exec(String(appointment.time || appointment.slot || ''));
  if (y == null || mo == null || da == null || !time) return null;
  return new Date(y, mo - 1, da, Number(time[1]), Number(time[2]));
};

// Normalize a native /api/appointments row into the same view shape the B.PRO
// projection uses, so both render through one code path. Native rows carry
// start_datetime (ISO) + customer_name/service + the backend-derived
// source/department tags.
const nativeToView = (row) => {
  const startIso = String(row.start_datetime || '');
  const dm = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(startIso);
  const dateStr = dm ? `${dm[1]}-${dm[2]}-${dm[3]}` : '';
  const timeStr = dm ? `${dm[4]}:${dm[5]}` : '';
  return {
    ...row,
    external_cita_id: row.appointment_id || row.bpro_cita_id || '',
    date: dateStr,
    time: timeStr,
    slot: timeStr,
    asesor_nombre: row.agent_name || '',
    asesor_id: row.agent_id || '',
    branch: row.branch || '',
    status: row.status || '',
    // Keep end_datetime available so the panel can show a real duration; the
    // rest of the native fields (customer_phone/service/notes/call_sid/…) already
    // flow through the spread above.
    end_datetime: row.end_datetime || '',
  };
};

// --- Detail-panel presentation helpers (pure + unit-testable) ---------------
// The calendar detail drawer must surface EVERYTHING a loaded appointment row
// actually carries. These helpers derive the display strings without mutating
// the row, and let the tests assert formatting independently of React.

// Full es-MX date: "jueves 4 de septiembre de 2026, 9:00 a.m." (never "4/9/2026").
export const formatFullDate = (date, locale) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString(locale, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const time = date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
};

// Whole-minute duration between two ISO datetimes -> "45 min" / "1 h 30 min".
export const formatDuration = (startIso, endIso, t) => {
  const s = Date.parse(String(startIso || ''));
  const e = Date.parse(String(endIso || ''));
  if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return '';
  const mins = Math.round((e - s) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const minLabel = (t && t('calendar.minutesShort')) || 'min';
  const hrLabel = (t && t('calendar.hoursShort')) || 'h';
  if (h && m) return `${h} ${hrLabel} ${m} ${minLabel}`;
  if (h) return `${h} ${hrLabel}`;
  return `${m} ${minLabel}`;
};

// Human origen label. Native rows carry source (voice|whatsapp) + optional
// bpro_origin; B.PRO rows are the shared dealer agenda (manual/B.PRO). Returns a
// stable key the i18n layer renders.
export const originKey = (appt) => {
  if (!appt) return 'manual';
  if (appt._source === SOURCE_BPRO) return 'bpro';
  const src = String(appt.source || '').trim().toLowerCase();
  if (src === 'whatsapp') return 'iaWhatsapp';
  if (src === 'voice') return 'iaVoice';
  return 'manual';
};

// Agenda-row title (fix-calendar-ia-appointment-title oct05, Roger 11:49): an
// IA-created (native aivoice) cita is titled after its CUSTOMER, e.g.
// "Cita Rogelio — Servicio 36 mil" (fallback "Cita Rogelio"). Before, postventa
// IA rows rarely carry placas/VIN/service_reason, so the B.PRO-oriented label came
// back empty and fell through to the literal "Cita B.PRO" — mislabelling our own
// bookings as dealer-agenda rows. B.PRO-imported rows keep their own label
// (placas · VIN · motivo / cliente · vehículo) and the "Cita B.PRO" fallback; the
// source badge (IA / B.PRO) is rendered separately and is unchanged.
export const agendaRowTitle = (appt, t, bproLabel = '') => {
  const tr = (key, fallback) => {
    const v = t ? t(key) : '';
    return v && v !== key ? v : fallback;
  };
  if (!appt) return '';
  if (appt._source === SOURCE_BPRO) {
    return bproLabel || tr('calendar.bproAppointment', 'Cita B.PRO');
  }
  const clean = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  const name = clean(appt.customer_name);
  const what = clean(appt.service || appt.service_package || appt.vehicle_interest
    || appt.service_reason || appt.vehicle);
  const prefix = tr('calendar.detailTitle', 'Cita');
  // ventas-prueba-de-manejo-oct15: a ventas cita booked WITH a test drive
  // carries the boolean `prueba_de_manejo` on the row; suffix the title so the
  // asesor sees it at a glance. Strict true only (legacy rows have no key).
  const suffix = appt.prueba_de_manejo === true
    ? ` \u00b7 ${tr('calendar.pruebaDeManejo', 'Prueba de manejo')}` : '';
  if (name && what) return `${prefix} ${name} \u2014 ${what}${suffix}`;
  if (name) return `${prefix} ${name}${suffix}`;
  const ia = tr('calendar.iaAppointment', 'Cita IA');
  return (what ? `${ia} \u2014 ${what}` : ia) + suffix;
};

// tel:/wa.me link for a phone (digits only, keep leading + when present).
export const telHref = (phone) => {
  const raw = String(phone || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : '';
};
export const waHref = (phone) => {
  const raw = String(phone || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '';
};

// The fields the panel WANTS to show for a given row. Returns the list of keys
// whose value is absent on the row, so we can render '—' AND log the gap for the
// booking beast (which owns why the backend never wrote them).
export const detailMissingFields = (appt) => {
  if (!appt) return [];
  const present = (v) => {
    const s = String(v == null ? '' : v).trim();
    return s !== '' && s !== '—';
  };
  const wanted = {
    customer_name: appt.customer_name,
    customer_phone: appt.customer_phone,
    vehicle: appt.vehicle_interest || appt.vehicle || appt.service,
    vin: appt.vin,
    placas: appt.placas,
    department: appt._department || appt.department,
    sucursal: appt._sucursalLabel || appt.sucursal || appt.branch,
    asesor: appt.asesor_nombre || appt.asesor_id,
    duration: appt.end_datetime,
    notes: appt.notes || appt.service_reason || appt.service,
  };
  return Object.keys(wanted).filter((k) => !present(wanted[k]));
};

// Color-blind-safe badge palettes (blue = B.PRO, amber = aivoice; teal = Ventas,
// violet = Postventa). Hue + text keep them distinguishable without relying on
// red/green.
const SOURCE_BADGE = {
  [SOURCE_BPRO]: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
  [SOURCE_AIVOICE]: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200',
};
const DEPT_BADGE = {
  [DEPT_VENTAS]: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200',
  [DEPT_POSTVENTA]: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200',
};

// Icon per deep-link action key (citaLinks.resolveCitaLinks).
const LINK_ICON = {
  client: UserCircleIcon,
  campaign: MegaphoneIcon,
  whatsapp: ChatBubbleLeftRightIcon,
  call: PhoneArrowUpRightIcon,
};

function CalendarPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const reviewUser = auth?.user;
  const reviewScope = reviewIdentity(reviewUser);
  const reviewVisible = Boolean(reviewScope);
  const [reviewSelection, setReviewSelection] = useState(null);
  const [reviewListScope, setReviewListScope] = useState(null);
  const openReview = (appointmentId) => { setReviewListScope(null); setReviewSelection({ appointmentId, identity: reviewScope }); };
  const [reviewRevision, setReviewRevision] = useState(0);
  useEffect(() => { setReviewSelection(null); setReviewListScope(null); }, [reviewScope]);
  const reviewChanged = (row) => {
    setNativeAppointments((rows) => rows.map((old) => old.appointment_id === row.appointment_id ? { ...old, ...row } : old));
    setReviewRevision((n) => n + 1);
  };
  const [searchParams] = useSearchParams();
  const [monthDate, setMonthDate] = useState(startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [bproAppointments, setBproAppointments] = useState([]);
  const [nativeAppointments, setNativeAppointments] = useState([]);
  const [calendarMeta, setCalendarMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerAppt, setDrawerAppt] = useState(null);
  // feat-calendar-filters-sep5: the top-right Ventas|Postventa toggle is gone;
  // the Ventas/Postventa chips in the Filtros row are now the ONLY department
  // control (multi-select, both ON by default). `filters` holds the source AND
  // department chip state; the active departments drive the B.PRO backend read.
  const [filters, setFilters] = useState(() => {
    const base = loadFilters();
    // Back-compat deep link: /calendar?department=postventa (or ?dept=ventas)
    // used to flip the old toggle. Map it onto chip state so those links keep
    // landing on the right department view.
    const dep = String(searchParams.get('department') || searchParams.get('dept') || '')
      .trim().toLowerCase();
    if (dep === DEPT_VENTAS || dep === DEPT_POSTVENTA) {
      return { ...base, [DEPT_VENTAS]: dep === DEPT_VENTAS, [DEPT_POSTVENTA]: dep === DEPT_POSTVENTA };
    }
    return base;
    // searchParams is read once for the initial deep link; ongoing changes go
    // through the chips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });
  // Data-driven sucursal options (live API list, with a static fallback so the
  // chips render even when B.PRO is down / the month has 0 rows).
  const [sucursalOptionsApi, setSucursalOptionsApi] = useState([]);
  // Multi-select sucursal chip state { <key>: boolean }; absent key => ON.
  const [sucursalFilters, setSucursalFilters] = useState(() => loadSucursalFilters());
  const locale = i18n.language === 'es' ? 'es-MX' : 'en-US';

  // Active departments derived from the chips. Both ON => read both from B.PRO;
  // neither ON => read neither (empty grid). This replaces the old single
  // `department` state that the removed toggle drove.
  const activeDepartments = useMemo(() => {
    const deps = [];
    if (filters[DEPT_VENTAS]) deps.push(DEPT_VENTAS);
    if (filters[DEPT_POSTVENTA]) deps.push(DEPT_POSTVENTA);
    return deps;
  }, [filters]);
  // Stable dependency key so the fetch effect re-runs when the active set changes
  // without depending on the whole `filters` object (source toggles are
  // client-side only and must NOT trigger a refetch).
  const activeDepartmentsKey = activeDepartments.join(',');

  // Branch targets for the B.PRO fetch. Uses ONLY the live API list + static
  // fallback (never row-derived, since rows don't exist before the fetch), so the
  // read fans out across the tenant's sucursales even with 0 currently-loaded
  // rows. Display chips (below) additionally fold in any sucursal seen in rows.
  const sucursalFetchOptions = useMemo(
    () => deriveSucursalOptions(sucursalOptionsApi, []),
    [sucursalOptionsApi]
  );
  const sucursalFetchOptionsKey = sucursalFetchOptions.map((o) => o.value).join(',');

  const sourceLabel = (s) => (s === SOURCE_BPRO ? t('calendar.sourceBpro') : t('calendar.sourceAivoice'));
  const deptLabel = (d) => (d === DEPT_POSTVENTA ? t('calendar.postventa') : t('calendar.ventas'));
  const originLabel = (appt) => t(`calendar.origin.${originKey(appt)}`);

  // When a cita drawer opens, log which fields the loaded row was MISSING so the
  // booking beast (beast-postventa-wa-booking-wrong-fields-sep2) can see exactly
  // what the backend never wrote. Panel-only: we never mutate booking data.
  useEffect(() => {
    if (!drawerAppt) return;
    const missing = detailMissingFields(drawerAppt);
    if (missing.length) {
      // eslint-disable-next-line no-console
      console.warn(
        `[calendar] cita ${drawerAppt.external_cita_id || '(no id)'} missing fields: ${missing.join(', ')}`
      );
    }
  }, [drawerAppt]);

  // Load the live sucursal option list once (data-driven, per-tenant). A failure
  // (B.PRO down / testing ECS at 0) simply leaves it empty; deriveSucursalOptions
  // then supplies the static fallback so the chips still render.
  useEffect(() => {
    let alive = true;
    if (typeof getBproSucursales !== 'function') return undefined;
    Promise.resolve()
      .then(() => getBproSucursales())
      .then((res) => { if (alive) setSucursalOptionsApi(res?.data?.sucursales || []); })
      .catch(() => { if (alive) setSucursalOptionsApi([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    const first = startOfMonth(monthDate);
    const last = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    setLoading(true);
    setError('');
    setDrawerAppt(null);

    // Native aivoice appointments (best-effort; never blocks the B.PRO view).
    // Guarded so a build/mock without listAppointments simply yields none.
    let nativePromise = Promise.resolve([]);
    if (typeof listAppointments === 'function') {
      try {
        const p = listAppointments({ start_from: `${isoDay(first)}T00:00:00`, start_to: `${isoDay(last)}T23:59:59` });
        if (p && typeof p.then === 'function') {
          nativePromise = p
            .then((res) => (res?.data?.appointments || []).map(nativeToView).map(tagAppointment))
            .catch(() => []);
        }
      } catch (_e) {
        nativePromise = Promise.resolve([]);
      }
    }

    nativePromise.then((native) => { if (alive) setNativeAppointments(native); });

    // B.PRO reads. Department is driven by the Ventas/Postventa chips now: we read
    // once per ACTIVE department (both by default) and merge. Sucursal is loaded
    // cross-branch (one read per known sucursal option, merged) and then filtered
    // client-side by the sucursal chips, so toggling a sucursal never refetches.
    // Each row is tagged with its own sucursal server-side, so counts stay correct.
    const depsToRead = activeDepartments.length ? activeDepartments : [];
    const branchTargets = sucursalFetchOptions.length > 1
      ? sucursalFetchOptions.map((o) => o.value)
      : [undefined];
    const readOne = (dep, suc) => getBproCalendar({
      date: isoDay(first), date_to: isoDay(last), department: dep,
      ...(suc ? { sucursal: suc } : {}),
    })
      .then((res) => res?.data || {})
      .catch(() => ({ source: 'bpro', available: null, error: 'bproUnavailable', appointments: [] }));

    // Cartesian of active departments × branch targets.
    const readPlan = [];
    depsToRead.forEach((dep) => branchTargets.forEach((suc) => readPlan.push([dep, suc])));

    Promise.all(readPlan.map(([dep, suc]) => readOne(dep, suc)))
      .then((views) => {
        if (!alive) return;
        // Meta: single-branch view when one target; "Todas" summary otherwise.
        const anyAvailable = views.some((v) => v.available === true);
        // Distinguish a feature that is simply OFF for this tenant (gate_disabled)
        // from a REAL B.PRO/LAN outage (bproUnavailable/no_token/branch_unresolved).
        // Only a real outage should raise the "B.PRO no está disponible" banner; an
        // off feature must render a neutral empty state, never a scary error.
        const allGateOff = views.length > 0 && views.every((v) => v.error === 'gate_disabled');
        if (views.length === 1) {
          setCalendarMeta(views[0]);
        } else {
          setCalendarMeta({ source: 'bpro', available: anyAvailable ? true : null, branch: t('calendar.allSucursales') });
        }
        if (!anyAvailable) {
          setBproAppointments([]);
          // Feature-off => neutral (no banner). Real outage => banner.
          setError(allGateOff ? '' : 'bproUnavailable');
          return;
        }
        const merged = views.flatMap((view) => (view.appointments || []).map((a) => tagAppointment({
          ...a,
          source: SOURCE_BPRO,
          appointment_id: a.external_cita_id,
        })));
        // Dedupe by (cita id + sucursal). We now fan out one read per active
        // department × per sucursal, so the SAME B.PRO cita can be returned by
        // more than one read (e.g. a backend that ignores the sucursal param, or
        // a cita visible under both department queries). Keeping a stable key
        // prevents duplicate agenda rows / React key collisions.
        const seen = new Set();
        const deduped = [];
        merged.forEach((a) => {
          const key = `${a.external_cita_id || ''}|${a._sucursal || ''}|${a._department || ''}`;
          if (seen.has(key)) return;
          seen.add(key);
          deduped.push(a);
        });
        setBproAppointments(deduped);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // Refetch only when the month, active departments, or the branch option list
    // change. Sucursal CHIP toggles filter client-side and must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDepartmentsKey, monthDate, sucursalFetchOptionsKey]);

  // Merged, tagged, then date-parsed. B.PRO + native aivoice in one list so the
  // page can show a real source distinction.
  const allAppointments = useMemo(
    () => [...bproAppointments, ...nativeAppointments].map((a) => ({ ...a, _date: appointmentDate(a) })),
    [bproAppointments, nativeAppointments]
  );

  // Display sucursal chip options: fetch options PLUS any sucursal actually seen
  // in the loaded rows (so a row's sucursal is always filterable): "Villahermosa:
  // Deportiva", "Cárdenas", "Comalcalco". Jocelin 12:48 CST oct22: Tabasco 2000
  // is a sales site with no taller, so the #2214 "Villahermosa: Tabasco 2000"
  // chip is gone and a cita requested for it folds into Deportiva (its db).
  // Renders even with 0 rows via the static fallback inside deriveSucursalOptions.
  const sucursalChipOptions = useMemo(
    () => deriveSucursalChipOptions(sucursalOptionsApi, allAppointments),
    [sucursalOptionsApi, allAppointments]
  );

  // Sucursal-scoped list first (so the source/department counts reflect the
  // selected sucursales), then source/department filtering.
  const sucursalScoped = useMemo(
    () => filterBySucursalChips(allAppointments, sucursalFilters),
    [allAppointments, sucursalFilters]
  );

  const counts = useMemo(() => computeCounts(sucursalScoped), [sucursalScoped]);
  // "sincronizado hace X min" note from the freshest synced B.PRO row (SYNC path).
  const syncedAgo = useMemo(() => syncedAgoLabel(allAppointments), [allAppointments]);
  // How many SYNCED B.PRO citas are actually loaded. These native rows (source=
  // "bpro") render even when the LIVE B.PRO read is down, so their presence must
  // OVERRIDE the "B.PRO no está disponible" outage banner: synced data IS present.
  const syncedBproCount = useMemo(() => bproSyncedCount(allAppointments), [allAppointments]);
  // Schedule-aware verdict (verify-bpro-sync-stale-saturday-oct10): "synced 5 h
  // ago" INSIDE the 07:00-19:40 Mon-Sat window means the push is stalled, so the
  // pill turns amber and says "sync atrasado" instead of a reassuring blue note.
  const syncStatus = useMemo(() => bproSyncStatus(allAppointments), [allAppointments]);
  const syncOverdue = !!(syncStatus && syncStatus.overdue);
  const syncPillCls = syncOverdue
    ? 'rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-900/50 dark:text-amber-100'
    : 'rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
  const syncOverdueNote = syncOverdue
    ? ` · ${t('calendar.bproSyncOverdue', { missed: syncStatus.missed })}`
    : '';
  // Only raise the red outage banner when the live read failed AND there is zero
  // synced B.PRO data to fall back on. With synced citas present we show the green
  // "Sincronizado con B.PRO hace X min (N citas)" note instead of a fake outage.
  const showOutageBanner = error === 'bproUnavailable' && syncedBproCount === 0;
  // Per-sucursal counts computed over the FULL loaded list so each switcher chip
  // shows how many it controls regardless of the current selection.
  const sucursalCounts = useMemo(() => computeSucursalCounts(allAppointments), [allAppointments]);

  const visibleAppointments = useMemo(
    () => filterAppointments(sucursalScoped, filters),
    [sucursalScoped, filters]
  );

  // Toggle one sucursal chip. Default (absent) is ON, so the first click turns it
  // OFF. Persisted like the source/department chips.
  const toggleSucursal = (key) => {
    setSucursalFilters((prev) => {
      const currentlyOn = sucursalChipOn(prev, key);
      const next = { ...prev, [key]: !currentlyOn };
      saveSucursalFilters(next);
      return next;
    });
  };

  const toggleFilter = (key) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveFilters(next);
      return next;
    });
  };

  const byDay = useMemo(() => {
    const map = {};
    visibleAppointments.forEach((a) => {
      if (!a._date) return;
      const key = isoDay(a._date);
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [visibleAppointments]);

  // Per-day, per-sucursal counts for the day-cell tooltip ("Villahermosa 2 ·
  // Cárdenas 1"). Built over the VISIBLE (source/dept + sucursal filtered) list so
  // the tooltip matches the dots actually shown. Keyed by isoDay -> { <sucKey>: n }.
  const byDaySucursal = useMemo(() => {
    const map = {};
    visibleAppointments.forEach((a) => {
      if (!a._date) return;
      const dayKey = isoDay(a._date);
      const sucKey = a._sucursal || deriveSucursalKey(a);
      if (!map[dayKey]) map[dayKey] = {};
      map[dayKey][sucKey] = (map[dayKey][sucKey] || 0) + 1;
    });
    return map;
  }, [visibleAppointments]);

  // es-MX label for a sucursal key (uses the API options first, then the derived
  // label fallback). Drives the agenda header + day-cell tooltip text.
  const sucursalKeyLabel = (key) => {
    if (!key || key === SUCURSAL_UNKNOWN) return t('calendar.sinSucursal');
    const opt = sucursalChipOptions.find((o) => o.value === key);
    return opt ? opt.label : key;
  };

  // Tooltip text for a day cell: per-sucursal breakdown, e.g.
  // "Villahermosa 2 · Cárdenas 1". Empty when the day has no citas.
  const dayTooltip = (cell) => {
    const perSuc = byDaySucursal[isoDay(cell)] || {};
    const parts = Object.keys(perSuc)
      .sort((x, y) => sucursalKeyLabel(x).localeCompare(sucursalKeyLabel(y)))
      .map((k) => `${sucursalKeyLabel(k)} ${perSuc[k]}`);
    return parts.join(' \u00b7 ');
  };

  // Active-sucursal summary for the agenda header: "Todas" when every chip is ON,
  // otherwise the comma-joined labels of the ON sucursales (or "Ninguna" if all
  // off). Uses the chip state so it always matches what's rendered.
  const activeSucursalKeys = sucursalChipOptions
    .map((o) => o.value)
    .filter((v) => sucursalChipOn(sucursalFilters, v));
  const allSucursalesOn = activeSucursalKeys.length === sucursalChipOptions.length;
  const selectedSucursalLabel = allSucursalesOn
    ? t('calendar.allSucursales')
    : (activeSucursalKeys.map(sucursalKeyLabel).join(', ') || t('calendar.empty'));

  const cells = useMemo(() => buildMonthGrid(monthDate), [monthDate]);
  const selectedList = (byDay[isoDay(selectedDay)] || []).slice().sort(
    (a, b) => (a._date?.getTime() || 0) - (b._date?.getTime() || 0)
  );
  const weekdays = locale === 'es-MX'
    ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthLabel = monthDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const fmtTime = (d) => d?.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) || '';

  const navigateMonth = (delta) => {
    const next = addMonths(monthDate, delta);
    setMonthDate(next);
    setSelectedDay(next);
  };

  const detailLabel = (a) => (a._department === DEPT_POSTVENTA
    ? [a.placas, a.vin, a.service_reason].filter(Boolean).join(' · ')
    : [a.customer_name, a.vehicle_interest].filter(Boolean).join(' · '));

  const Badge = ({ className, children }) => (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${className}`}>{children}</span>
  );

  // Per-sucursal chip: colored badge carrying the short initial (V/C/Co) so a
  // cita's sucursal is legible at a glance AND in grayscale (color + text, never
  // color-only). Full label appended when `withLabel` (agenda rows / panel). Only
  // renders for a resolvable sucursal so untagged rows stay uncluttered.
  const SucursalChip = ({ appt, withLabel = false }) => {
    const key = appt._sucursal || deriveSucursalKey(appt);
    const label = appt._sucursalLabel;
    if (!key || key === SUCURSAL_UNKNOWN) return null;
    const accent = sucursalAccent(key);
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${accent.badge}`}
        title={label || key}
      >
        <span className={`inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-0.5 text-[8px] font-bold text-white ${accent.dot}`}>
          {accent.initial}
        </span>
        {withLabel ? (label || key) : null}
      </span>
    );
  };

  const FilterToggle = ({ filterKey, label, count, badgeClass }) => (
    <button
      type="button"
      onClick={() => toggleFilter(filterKey)}
      aria-pressed={!!filters[filterKey]}
      aria-label={`${t('calendar.filters')}: ${label}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${filters[filterKey]
        ? 'border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
        : 'border-gray-200 bg-gray-100 text-gray-400 line-through dark:border-gray-700 dark:bg-gray-900 dark:text-gray-600'}`}
    >
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${badgeClass}`} />
      {label}
      <span className="ml-0.5 rounded bg-gray-200 px-1 text-[10px] text-gray-700 dark:bg-gray-700 dark:text-gray-200">{count}</span>
    </button>
  );

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <CalendarDaysIcon className="h-7 w-7 text-emerald-600" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('calendar.title')}</h1>
        </div>
        <div className="flex items-center gap-2">
          {/* live-handoff v3: consent-based advisor call-backs. Every role sees the tray; the API scopes it. */}
          <FollowupQueue user={reviewUser} />
          {reviewVisible && <AdvisorReviewQueue user={reviewUser} revision={reviewRevision} onOpen={openReview}
            open={reviewListScope === reviewScope} onOpenChange={(open) => setReviewListScope(open ? reviewScope : null)} />}
        </div>
        {/* feat-calendar-filters-sep5: the redundant top-right Ventas|Postventa
            department toggle was removed. Department is now controlled ONLY by the
            Ventas/Postventa chips in the Filtros row below (multi-select). */}
      </div>

      {reviewVisible && reviewSelection?.identity === reviewScope && <AdvisorReviewPanel
        appointmentId={reviewSelection.appointmentId} user={reviewUser}
        onClose={() => setReviewSelection(null)} onChanged={reviewChanged}
        onBack={() => { setReviewSelection(null); setReviewListScope(reviewScope); }} />}

      <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-800 dark:bg-emerald-950/30">
        <div className="flex flex-wrap items-center gap-2 text-sm text-emerald-900 dark:text-emerald-200">
          <span className="font-semibold">{t('calendar.bproSource')}</span>
          {calendarMeta?.branch ? <span>· {calendarMeta.branch}</span> : null}
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs dark:bg-emerald-900">
            {t('calendar.sharedConflict')}
          </span>
          {syncedBproCount > 0 ? (
            <span className={syncPillCls} data-testid="calendar-bpro-synced" data-sync-overdue={syncOverdue ? 'true' : 'false'}>
              {t('calendar.bproSyncedWithCount', {
                ago: syncedAgo ? t(syncedAgo.key, syncedAgo.opts) : t('calendar.bproSyncedJustNow', { count: syncedBproCount }),
                count: syncedBproCount,
              })}{syncOverdueNote}
            </span>
          ) : syncedAgo ? (
            <span className={syncPillCls} data-testid="calendar-bpro-synced" data-sync-overdue={syncOverdue ? 'true' : 'false'}>
              {t(syncedAgo.key, syncedAgo.opts)}{syncOverdueNote}
            </span>
          ) : null}
        </div>
      </div>

      {/* Legend + filter toolbar (feat-calendar-source-department-filters-sep1) */}
      <div className="mb-4 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('calendar.legend')}</span>
            <Badge className={SOURCE_BADGE[SOURCE_BPRO]}>{t('calendar.sourceBpro')}</Badge>
            <Badge className={SOURCE_BADGE[SOURCE_AIVOICE]}>{t('calendar.sourceAivoice')}</Badge>
            <Badge className={DEPT_BADGE[DEPT_VENTAS]}>{t('calendar.ventas')}</Badge>
            <Badge className={DEPT_BADGE[DEPT_POSTVENTA]}>{t('calendar.postventa')}</Badge>
          </div>
          {/* Sucursal accents legend: the sucursal filter chips below already carry
              the color dot + initial + full name, so we do NOT repeat a separate
              accent legend here (dedupe, feat-calendar-filters-sep5). */}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2" aria-label={t('calendar.filters')}>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('calendar.filters')}:</span>
          <FilterToggle filterKey={SOURCE_BPRO} label={t('calendar.sourceBpro')} count={counts[SOURCE_BPRO]} badgeClass="bg-blue-500" />
          <FilterToggle filterKey={SOURCE_AIVOICE} label={t('calendar.sourceAivoice')} count={counts[SOURCE_AIVOICE]} badgeClass="bg-amber-500" />
          <span className="mx-1 h-4 w-px bg-gray-200 dark:bg-gray-700" />
          {/* Ventas/Postventa are now the ONLY department control (multi-select,
              both ON by default). Toggling one refetches that department from
              B.PRO (see the active-departments effect). */}
          <FilterToggle filterKey={DEPT_VENTAS} label={t('calendar.ventas')} count={counts[DEPT_VENTAS]} badgeClass="bg-teal-500" />
          <FilterToggle filterKey={DEPT_POSTVENTA} label={t('calendar.postventa')} count={counts[DEPT_POSTVENTA]} badgeClass="bg-violet-500" />
        </div>
        {/* Sucursal filter chips (feat-calendar-filters-sep5). Multi-select, all ON
            by default, ALWAYS rendered (static fallback) so they show even with 0
            rows / B.PRO down — counts then simply read 0. Each chip pairs its
            color+initial accent with the full name (color-blind safe) and doubles
            as the sucursal legend. */}
        <div className="mt-3 flex flex-wrap items-center gap-2" aria-label={t('calendar.sucursal')} data-testid="sucursal-chips">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('calendar.sucursal')}:</span>
          {sucursalChipOptions.map((opt) => {
            const accent = sucursalAccent(opt.value);
            const on = sucursalChipOn(sucursalFilters, opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleSucursal(opt.value)}
                aria-pressed={on}
                aria-label={`${t('calendar.sucursal')}: ${opt.label}`}
                data-testid={`sucursal-chip-${opt.value}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${on
                  ? 'border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                  : 'border-gray-200 bg-gray-100 text-gray-400 line-through dark:border-gray-700 dark:bg-gray-900 dark:text-gray-600'}`}
              >
                <span className={`inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-0.5 text-[8px] font-bold text-white ${accent.dot}`}>
                  {accent.initial}
                </span>
                {opt.label}
                <span className="ml-0.5 rounded bg-gray-200 px-1 text-[10px] text-gray-700 dark:bg-gray-700 dark:text-gray-200">{sucursalCounts[opt.value] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      {showOutageBanner ? (
        <div role="alert" className="mb-4 rounded-md bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {t(`calendar.${error}`)}
        </div>
      ) : null}

      {/* feat-calendar-ux-sep3: the right agenda column scrolls INDEPENDENTLY so a
          day with many citas never grows the page / pushes the month grid down.
          Both columns are capped to the viewport height minus the page chrome
          above (header + toolbars ~ 22rem); items-start keeps the month grid at its
          natural height while the agenda column fills and scrolls within the cap. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <button type="button" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateMonth(-1)} aria-label={t('calendar.prevMonth')}>
              <ChevronLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
            <div className="text-lg font-medium capitalize text-gray-900 dark:text-white">{monthLabel}</div>
            <button type="button" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateMonth(1)} aria-label={t('calendar.nextMonth')}>
              <ChevronRightIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {weekdays.map((w) => <div key={w} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell, idx) => {
              if (!cell) return <div key={`e-${idx}`} className="h-16" />;
              const dayItems = byDay[isoDay(cell)] || [];
              const count = dayItems.length;
              const selected = sameDay(cell, selectedDay);
              const hasBpro = dayItems.some((a) => a._source === SOURCE_BPRO);
              const hasAivoice = dayItems.some((a) => a._source === SOURCE_AIVOICE);
              // Distinct sucursales present this day (display order via the switcher
              // options, unknown last) -> one small accent dot each, so the month
              // grid shows sucursal mix at a glance. Tooltip carries exact counts.
              const daySucKeys = Array.from(
                new Set(dayItems.map((a) => a._sucursal || deriveSucursalKey(a)))
              )
                .filter((k) => k && k !== SUCURSAL_UNKNOWN)
                .sort((x, y) => sucursalKeyLabel(x).localeCompare(sucursalKeyLabel(y)));
              const tip = dayTooltip(cell);
              return (
                <button key={isoDay(cell)} type="button" onClick={() => setSelectedDay(cell)}
                  title={tip || undefined}
                  aria-label={tip ? `${cell.getDate()} \u2014 ${tip}` : undefined}
                  className={`h-16 rounded-md border text-left p-1 flex flex-col ${selected
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}>
                  <span className="text-xs text-gray-700 dark:text-gray-300">{cell.getDate()}</span>
                  {count > 0 ? (
                    <span className="mt-auto flex flex-wrap items-center gap-1 text-[10px] font-medium text-gray-600 dark:text-gray-300" data-testid={`day-dots-${isoDay(cell)}`}>
                      {hasBpro ? <span className="inline-block h-2 w-2 rounded-full bg-blue-500" /> : null}
                      {hasAivoice ? <span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> : null}
                      {/* Per-sucursal accent dots carry the initial so the sucursal
                          mix is readable without hovering AND without color-only. */}
                      {daySucKeys.map((k) => {
                        const accent = sucursalAccent(k);
                        return (
                          <span
                            key={k}
                            className={`inline-flex h-3 min-w-3 items-center justify-center rounded-full px-0.5 text-[7px] font-bold leading-none text-white ${accent.dot}`}
                          >
                            {accent.initial}
                          </span>
                        );
                      })}
                      {count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {/* Agenda del día — compact, self-scrolling list. Fixed max-height = viewport
            minus the chrome above; sticky day header stays pinned while the citas
            scroll under it (overflow-y-auto). Light/dark both keep the sticky bg. */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col max-h-[calc(100vh-14rem)] overflow-hidden"
          data-testid="agenda-column"
        >
          <h2 className="sticky top-0 z-10 bg-white dark:bg-gray-800 text-sm font-semibold text-gray-900 dark:text-white px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <span className="capitalize">
              {selectedDay.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
            {!loading && selectedList.length > 0 ? (
              <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
                {selectedList.length}
              </span>
            ) : null}
            {/* Agenda header shows which sucursal(es) are currently in view. */}
            {sucursalChipOptions.length > 1 ? (
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300" data-testid="agenda-sucursal-label">
                {selectedSucursalLabel}
              </span>
            ) : null}
          </h2>
          <div className="min-h-0 flex-1 overflow-y-auto p-4" data-testid="agenda-scroll">
          {loading ? <p className="text-sm text-gray-500">{t('calendar.loading')}</p>
            : selectedList.length === 0 ? <p className="text-sm text-gray-500">{t('calendar.empty')}</p>
            : <ul className="space-y-2">
              {selectedList.map((a) => {
                const sucKey = a._sucursal || deriveSucursalKey(a);
                const accent = sucursalAccent(sucKey);
                const knownSuc = sucKey && sucKey !== SUCURSAL_UNKNOWN;
                return (
                <li key={`${a._source}-${a._department}-${a._sucursal}-${a.external_cita_id}`}>
                  {/* Left border accent per sucursal (color + the chip's initial
                      keep it color-blind safe). Unknown sucursal -> neutral border. */}
                  <button type="button" onClick={() => setDrawerAppt(a)} data-testid={`agenda-row-${a.external_cita_id}`} className={`w-full text-left rounded-md border border-l-4 ${knownSuc ? accent.border : 'border-l-gray-200 dark:border-l-gray-700'} border-gray-200 dark:border-gray-700 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/40`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{fmtTime(a._date)}</span>
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">{a.status || '—'}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <Badge className={SOURCE_BADGE[a._source]}>{sourceLabel(a._source)}</Badge>
                      <Badge className={DEPT_BADGE[a._department]}>{deptLabel(a._department)}</Badge>
                      <SucursalChip appt={a} withLabel />
                    </div>
                    <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 truncate">{agendaRowTitle(a, t, detailLabel(a))}</div>
                    <div className="mt-1 text-xs text-gray-500 truncate">{a.asesor_nombre || a.asesor_id || t('calendar.receptionQueue')} · {a.branch}</div>
                  </button>
                </li>
                );
              })}
            </ul>}
          </div>
        </div>
      </div>

      <Transition.Root show={!!drawerAppt} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDrawerAppt(null)}>
          <div className="fixed inset-0 bg-black/40" />
          <div className="fixed inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Dialog.Panel className="pointer-events-auto w-screen max-w-md bg-white dark:bg-gray-800 shadow-xl">
                <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                  <Dialog.Title className="font-semibold text-gray-900 dark:text-white">{t('calendar.detailTitle')}</Dialog.Title>
                  <button type="button" className="p-2" onClick={() => setDrawerAppt(null)} aria-label={t('calendar.close')}><XMarkIcon className="h-5 w-5" /></button>
                </div>
                {drawerAppt ? (() => {
                  const a = drawerAppt;
                  const dash = '\u2014';
                  const val = (v) => {
                    const s = String(v == null ? '' : v).trim();
                    return s || dash;
                  };
                  const asesor = a.asesor_nombre || a.asesor_id || t('calendar.receptionQueue');
                  const duration = formatDuration(a.start_datetime, a.end_datetime, t);
                  const phone = a.customer_phone || a.phone || '';
                  const tel = telHref(phone);
                  const wa = waHref(phone);
                  const isPostventa = a._department === DEPT_POSTVENTA;
                  const vehicleParts = [a.marca, a.modelo, a.anio || a.year].filter(Boolean).join(' ');
                  const vehicle = isPostventa
                    ? (vehicleParts || a.vehicle || a.placas || '')
                    : (a.vehicle_interest || vehicleParts || a.vehicle || a.service || '');
                  // Notes/motivo: prefer explicit notes; for native ventas rows the
                  // free-text `service` already feeds vehicle, so don't echo it here.
                  const notes = a.notes || a.service_reason || a.motivo
                    || (isPostventa ? (a.service || '') : '');
                  const threadHref = a.wa_thread_url || a.thread_url || a.conversation_url
                    || a.transcript_url || a.call_url || '';
                  // feat-calendar-ux-sep3: in-app deep links (client / campaign /
                  // WhatsApp / call). Missing id -> disabled button + tooltip.
                  const citaLinks = resolveCitaLinks(a);
                  const goLink = (link) => {
                    if (link.disabled || !link.to) return;
                    setDrawerAppt(null);
                    navigate(link.to);
                  };
                  return (
                <div className="p-4 space-y-2.5 text-sm text-gray-700 dark:text-gray-300" data-testid="cita-detail">
                  {a.advisor_review_enrolled === true && hasReviewAccess(reviewUser, a) && <button
                    type="button" className="w-full rounded border border-amber-400 px-3 py-2 text-left"
                    onClick={() => { setDrawerAppt(null); openReview(a.appointment_id); }}>
                    {t("advisorReview.openDetail")} · {t("advisorReview." + a.advisor_review_status)}
                  </button>}
                  {/* datos step B oct21: the customer disputed a datum (WhatsApp) -> marker + the
                      customer's words + "Marcar atendido" (the advisor corrects it in B.PRO). */}
                  {isPostventa && <DatosDisputeMarker row={a} testId="cita-datos-dispute"
                    onChanged={(next) => { setDrawerAppt((d) => (d ? { ...d, ...next } : d)); reviewChanged(next); }} />}
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge className={SOURCE_BADGE[a._source]}>{sourceLabel(a._source)}</Badge>
                    <Badge className={DEPT_BADGE[a._department]}>{deptLabel(a._department)}</Badge>
                    <SucursalChip appt={a} withLabel />
                  </div>
                  {/* Full es-MX date, e.g. "jueves 4 de septiembre de 2026, 9:00 a.m." */}
                  <div className="flex items-center gap-2 capitalize" data-testid="cita-date">
                    <CalendarDaysIcon className="h-5 w-5 text-emerald-600 shrink-0" />
                    <span>{a._date ? formatFullDate(a._date, locale) : dash}{duration ? ` \u00b7 ${duration}` : ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-5 w-5 text-gray-400 shrink-0" />
                    <span><b>{t('calendar.advisor')}:</b> {val(asesor)}</span>
                  </div>
                  <div><b>{t('calendar.customer')}:</b> {val(a.customer_name)}</div>
                  {/* Phone with tap-to-call + WhatsApp shortcuts when present. */}
                  <div data-testid="cita-phone">
                    <b>{t('calendar.phone')}:</b>{' '}
                    {phone ? (
                      <span className="inline-flex items-center gap-2">
                        {tel ? <a className="text-emerald-700 dark:text-emerald-400 underline" href={tel}>{phone}</a> : <span>{phone}</span>}
                        {wa ? <a className="text-emerald-700 dark:text-emerald-400 underline" href={wa} target="_blank" rel="noopener noreferrer">WhatsApp</a> : null}
                      </span>
                    ) : dash}
                  </div>
                  <div><b>{t('calendar.vehicle')}:</b> {val(vehicle)}</div>
                  {isPostventa ? <>
                    <div><b>{t('calendar.plates')}:</b> {val(a.placas)}</div>
                    <div><b>VIN:</b> {val(a.vin)}</div>
                    {/* taxi-factura-oct20: the two optional post-booking answers (strict
                        booleans on the row; "—" when the customer was not asked / unclear). */}
                    <div data-testid="cita-extras">
                      {bookingExtrasEntries(a).map(({ key, value }, i) => (
                        <span key={key} data-testid={`cita-extra-${key}`}>
                          {i > 0 ? ' \u00b7 ' : ''}
                          <b>{key === 'taxi' ? t('calendar.taxi') : t('calendar.factura')}:</b>{' '}
                          {value === true ? t('common.yes') : value === false ? t('common.no') : dash}
                        </span>
                      ))}
                    </div>
                    {/* td453: datos corrected by the customer (WhatsApp reply / voice readback) */}
                    {formatDatosUpdates(a, (k, f) => t(k, f)) ? (
                      <div data-testid="cita-datos-updates">{formatDatosUpdates(a, (k, f) => t(k, f))}</div>
                    ) : null}
                  </> : null}
                  <div><b>{t('calendar.department')}:</b> {deptLabel(a._department)}</div>
                  <div data-testid="cita-sucursal"><b>{t('calendar.sucursal')}:</b> {val(a._sucursalLabel || a.sucursal || a.branch)}</div>
                  <div><b>{t('calendar.originField')}:</b> {originLabel(a)}</div>
                  <div><b>{t('calendar.status')}:</b> {val(a.status)}</div>
                  <div><b>{t('calendar.notes')}:</b> {val(notes)}</div>
                  {threadHref ? (
                    <div>
                      <a className="text-emerald-700 dark:text-emerald-400 underline" href={threadHref} target="_blank" rel="noopener noreferrer">
                        {a._source === SOURCE_BPRO ? t('calendar.openThread') : t('calendar.openIaThread')}
                      </a>
                    </div>
                  ) : null}
                  {/* Deep-link actions: jump to the client / campaign / WhatsApp
                      thread / call so the operator can see the transcript and how
                      the call went (Roger 2026-09-03). Disabled + tooltip when the
                      linking id is missing ("sin registro vinculado"). */}
                  <div className="pt-3 mt-1 border-t border-gray-100 dark:border-gray-700 grid grid-cols-1 gap-2" data-testid="cita-links">
                    {citaLinks.map((link) => {
                      const Icon = LINK_ICON[link.key] || UserIcon;
                      return (
                        <button
                          key={link.key}
                          type="button"
                          onClick={() => goLink(link)}
                          disabled={link.disabled}
                          data-testid={`cita-link-${link.key}`}
                          title={link.disabled ? t(link.reason) : undefined}
                          aria-disabled={link.disabled}
                          className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition ${link.disabled
                            ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-600'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:hover:bg-emerald-900/50'}`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          {t(link.label)}
                        </button>
                      );
                    })}
                  </div>
                  <div className="pt-2 text-xs text-gray-400">
                    {a._source === SOURCE_BPRO
                      ? `B.PRO #${a.external_cita_id} \u00b7 ${a.interfaz || dash}`
                      : `${t('calendar.sourceAivoice')} \u00b7 ${a.external_cita_id || dash}`}
                  </div>
                </div>
                  );
                })() : null}
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

export default CalendarPage;
